import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSubscriptionPolicies,
  runRenewalEvaluation,
  renewSubscription,
  approveRenewal,
  rejectRenewal,
} from '../services/api';
import { formatDate, StatusBadge } from '../utils/helpers';
import { RefreshCw, Plus, ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const RENEWAL_MODE_LABELS = {
  AUTO: 'Auto Renew',
  BUYER_APPROVAL_REQUIRED: 'Manual Approval',
  MANUAL: 'Manual',
};

function renewalLabel(mode) {
  return RENEWAL_MODE_LABELS[mode] || mode?.replace(/_/g, ' ') || '—';
}

export default function AutoRenew() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('policies');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [actionId, setActionId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getSubscriptionPolicies()
      .then(r => setData(r.data))
      .catch(err => toast.error(err.response?.data?.error || 'Failed to load renewal policies'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleEvaluate = async () => {
    setEvaluating(true);
    try {
      const res = await runRenewalEvaluation();
      toast.success(res.data.message || 'Evaluation complete');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Evaluation failed');
    } finally {
      setEvaluating(false);
    }
  };

  const runAction = async (configId, action) => {
    setActionId(configId);
    try {
      if (action === 'renew') {
        const res = await renewSubscription(configId);
        toast.success(res.data.message || 'Renewed');
      } else if (action === 'approve') {
        await approveRenewal(configId);
        toast.success('Renewal approved');
      } else if (action === 'reject') {
        const reason = window.prompt('Reason for rejection (optional):');
        if (reason === null) return;
        await rejectRenewal(configId, { reason: reason || 'Rejected' });
        toast.success('Renewal rejected');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally {
      setActionId(null);
    }
  };

  const summary = data?.summary || { policies: 0, pending_approval: 0, needs_setup: 0, cancelled: 0 };
  const policies = (data?.policies || []).filter(p => p.config_id && !p.cancelled_at);
  const pending = data?.pending_queue || [];
  const needsSetup = data?.needs_setup || [];

  const tabs = [
    { id: 'policies', label: 'Policies', count: summary.policies },
    { id: 'pending', label: 'Pending Queue', count: summary.pending_approval },
    { id: 'setup', label: 'Needs Setup', count: summary.needs_setup },
  ];

  const rows = tab === 'policies' ? policies : tab === 'pending' ? pending : needsSetup;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Auto Renew</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Manage subscription renewal policies, run renewal evaluation, and approve or reject renewals.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={handleEvaluate} disabled={evaluating}
            className="btn-secondary text-sm">
            <RefreshCw size={14} className={evaluating ? 'animate-spin' : ''}/>
            {evaluating ? 'Running…' : 'Run Evaluation'}
          </button>
          <button type="button" onClick={() => navigate('/payment-terms')}
            className="btn-primary text-sm">
            <Plus size={14}/> New Policy
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'POLICIES', value: summary.policies },
          { label: 'PENDING APPROVAL', value: summary.pending_approval },
          { label: 'NEEDS SETUP', value: summary.needs_setup },
          { label: 'CANCELLED', value: summary.cancelled },
        ].map(card => (
          <div key={card.label} className="card p-4">
            <div className="text-xs font-semibold text-gray-400 tracking-wide">{card.label}</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b flex gap-1 flex-wrap">
          {tabs.map(t => (
            <button key={t.id} type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                tab === t.id
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}>
              {t.label}
              {t.count > 0 && (
                <span className="ml-1.5 text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full">
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {['Sales Order', 'Payment Term', 'Status', 'Mode', 'Notice', 'Inst.', 'Valid Upto', 'Actions'].map(h => (
                  <th key={h} className="table-th">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="table-td text-center text-gray-400 py-10">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="table-td text-center py-12">
                    <p className="text-gray-500 font-medium">
                      {tab === 'setup'
                        ? 'All subscription terms have renewal settings configured.'
                        : tab === 'pending'
                          ? 'No renewals pending approval.'
                          : 'No active renewal policies yet.'}
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      Create a <strong>Subscription</strong> payment term on a Sales Order and configure renewal settings.
                    </p>
                    <button type="button" onClick={() => navigate('/purchase-orders')}
                      className="btn-primary text-sm mt-4 mx-auto">
                      Go to Sales Orders
                    </button>
                  </td>
                </tr>
              ) : rows.map(row => (
                <tr key={row.term_id} className="hover:bg-gray-50">
                  <td className="table-td">
                    {row.po_id ? (
                      <button type="button"
                        onClick={() => navigate(`/purchase-orders/${row.po_id}`)}
                        className="text-primary-600 hover:underline font-mono text-sm">
                        {row.po_number || row.po_id.slice(0, 8)}
                      </button>
                    ) : (
                      <span className="text-gray-400 italic text-sm">Not linked</span>
                    )}
                    {row.po_title && (
                      <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[140px]">{row.po_title}</div>
                    )}
                  </td>
                  <td className="table-td">
                    <button type="button"
                      onClick={() => navigate(`/payment-terms/${row.term_id}`)}
                      className="font-medium text-gray-900 hover:text-primary-600 text-sm text-left">
                      {row.term_name}
                    </button>
                  </td>
                  <td className="table-td"><StatusBadge status={row.term_status} /></td>
                  <td className="table-td text-sm">
                    {row.renewal_mode
                      ? renewalLabel(row.renewal_mode)
                      : <span className="text-amber-600">Not configured</span>}
                  </td>
                  <td className="table-td text-sm text-gray-600">
                    {row.renewal_notice_days != null ? `${row.renewal_notice_days} days` : '—'}
                  </td>
                  <td className="table-td text-sm text-center">{row.installment_count || 0}</td>
                  <td className="table-td text-sm">{row.end_date ? formatDate(row.end_date) : '—'}</td>
                  <td className="table-td">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {row.po_id && (
                        <button type="button" title="Open SO"
                          onClick={() => navigate(`/purchase-orders/${row.po_id}`)}
                          className="btn-secondary text-xs py-1 px-2">
                          <ExternalLink size={12}/> SO
                        </button>
                      )}
                      {!row.config_id && (
                        <button type="button"
                          onClick={() => navigate(`/payment-terms/${row.term_id}`)}
                          className="btn-primary text-xs py-1 px-2">
                          Configure
                        </button>
                      )}
                      {row.config_id && row.renewal_mode === 'AUTO' && !row.cancelled_at && (
                        <button type="button" disabled={actionId === row.config_id}
                          onClick={() => runAction(row.config_id, 'renew')}
                          className="btn-primary text-xs py-1 px-2">
                          <RefreshCw size={12}/>
                          {actionId === row.config_id ? '…' : 'Renew'}
                        </button>
                      )}
                      {row.config_id && row.renewal_mode === 'BUYER_APPROVAL_REQUIRED' && !row.cancelled_at && (
                        <>
                          <button type="button" disabled={actionId === row.config_id}
                            onClick={() => runAction(row.config_id, 'approve')}
                            className="btn-primary text-xs py-1 px-2">
                            <CheckCircle size={12}/>
                          </button>
                          <button type="button" disabled={actionId === row.config_id}
                            onClick={() => runAction(row.config_id, 'reject')}
                            className="btn-secondary text-xs py-1 px-2 text-red-600">
                            <XCircle size={12}/>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
